import { FaExpand, FaFileExport, FaFilter, FaMinimize, FaSort, FaTableColumns } from "react-icons/fa6";
import { ButtonType1 } from "../../../styles/buttons.style";
import PageController from "../../PageController";
import Select from "../../Select";
import MultiSelect from "../../MultiSelect";
import cssVars from "../../../styles/cssVariables.vars";
import { MutableRefObject, ReactNode } from "react";
import ExportTableDialog from "../ExportTableDialog";
import { TableColumn } from "../types";
import FiltersModal from "../FiltersModal";
import StyledTableHeader from "./styles";
import { defaultSelect50 } from "../../../components-variants/defaultInputs";
import { CustomStateHook } from "../../../Hooks/State.hook";
import { FilterWithPaginationQueryParameters } from "../../../interfaces/generics.types";

type TableHeaderProps<T> = {
    rowsState: CustomStateHook<Array<T>>;
    modalState: CustomStateHook<ReactNode>;
    loadingState: CustomStateHook<boolean>;
    displayColumnsState: CustomStateHook<Array<TableColumn<T>>>;
    displayActionsState: CustomStateHook<boolean>;
    isMaximizedState: CustomStateHook<boolean>;
    currentPage: number;
    nextPage: boolean;
    prevPage: boolean;
    rowsCount: number;
    tableName: string;
    tableRef: MutableRefObject<null | HTMLTableElement>;
    columns: Array<TableColumn<T>>;
    haveActions: boolean;
    $modalFiltersWidth: number;
    $fontSize: number;
    $tableWidth: string;
    $tableHeight: string;
    enableAutoUpdate: boolean;
    autoUpdateState: CustomStateHook<boolean>;
    autoUpdateIntervalState: CustomStateHook<number>;
    appliedFiltersState: CustomStateHook<FilterWithPaginationQueryParameters<T>>;
}

function TableHeader<T>({
    enableAutoUpdate,
    rowsState,
    modalState,
    loadingState,
    displayColumnsState,
    displayActionsState,
    isMaximizedState,
    autoUpdateState,
    autoUpdateIntervalState,
    appliedFiltersState,
    nextPage,
    prevPage,
    rowsCount,
    haveActions,
    tableName,
    tableRef,
    columns,
    $modalFiltersWidth,
    $fontSize,
}: TableHeaderProps<T>) {

    const openExportTableModal = () => modalState.set(<ExportTableDialog<T>
        modalState={modalState}
        tableName={tableName}
        tableRef={tableRef}
        columns={columns}
        rows={rowsState.value}
    />);

    const openFiltersModal = () => modalState.set(<FiltersModal<T>
        $modalFiltersWidth={$modalFiltersWidth}
        columns={columns}
        filtersModalState={modalState}
        rowsState={rowsState}
        tableName={tableName}
        appliedFiltersState={appliedFiltersState}
    />);

    const displayColumnsOptions = haveActions ?
        [{ name: "Ações", value: "actions" }, ...columns.map(c => ({ name: c.header || String(c.key), value: String(c.key) }))]
        :
        columns.filter(c => !c.hidden).map(c => ({ name: c.header || String(c.key), value: String(c.header || String(c.key)) }));

    const sortColumnOptions = columns.filter(c => !c.hidden).map(c => ({ name: c.header || String(c.key), value: String(c.key) }))

    const onChangeDisplayColumns = (v: string[]) => {
        displayActionsState.set(v.some(o => o === "actions"));
        displayColumnsState.set(columns.filter(c => v.includes(c.header || String(c.key))));
    }

    const onChangeSortByColumn = (v: string | null) => {
        appliedFiltersState.set(prev => ({ ...prev, "ORDENAR_POR": v || undefined as any }));
    }

    const onChangeSortTypeColumn = (v: boolean | null) => {
        appliedFiltersState.set(prev => ({ ...prev, "ORDEM_ASC": Boolean(v) }));
    }

    const onClickMaximizeButton = () => isMaximizedState.set(prev => !prev);

    const totalNumberOfFilters = columns.filter(c => c.filter).length;
    const numberOfAppliedFilters = columns.filter(c => c.filter && Object.keys(appliedFiltersState.value).includes(c.key as string)).length;
    const filtersText = `Filtros (${numberOfAppliedFilters}/${totalNumberOfFilters})`;
    const haveFilters: boolean = columns.some(c => !!c.filter);

    return (
        <StyledTableHeader $fontSize={$fontSize}>
            <div>
                <button title="Maximizar tabela" onClick={onClickMaximizeButton} style={{ cursor: "pointer" }}>
                    {isMaximizedState.value ? <FaMinimize /> : <FaExpand />}
                </button>
                {
                    rowsCount ?
                        <span> {rowsCount.toLocaleString()} {rowsCount > 1 ? "registros encontrados" : "registro encontrado"} </span>
                        :
                        <span> Nenhum registro encontardo </span>
                }
            </div>
            <div>
                {
                    enableAutoUpdate &&
                    <>

                        <div className="auto-update">
                            <span>
                                Atualizar
                            </span>
                            <input type="checkbox" onChange={(e) => autoUpdateState.set(e.target.checked)} />
                            {
                                autoUpdateState.value &&
                                <Select
                                    {...defaultSelect50}
                                    $focusColor={cssVars.colorGrey[0]}
                                    placeholder="Intervalo"
                                    $width="10rem"
                                    options={[
                                        { value: 5, name: "5 segundos" },
                                        { value: 15, name: "15 segundos" },
                                        { value: 30, name: "30 segundos" },
                                        { value: 60, name: "1 minuto" },
                                        { value: 300, name: "5 minutos" },
                                        { value: 600, name: "10 minutos" },
                                    ]}
                                    defaultValue={{ value: 5, name: "5 segundos" }}
                                    onChange={(v) => autoUpdateIntervalState.set(v || autoUpdateIntervalState.defaultValue)}
                                />
                            }
                        </div>
                    </>

                }
                <PageController
                    appliedFiltersState={appliedFiltersState}
                    next={nextPage}
                    prev={prevPage}
                />
                <ButtonType1 onClick={openExportTableModal}>
                    <FaFileExport />
                    <p> Exportar </p>
                </ButtonType1>
                {
                    haveFilters &&
                    <ButtonType1 onClick={openFiltersModal} disabled={loadingState.value}>
                        <FaFilter />
                        <p> {filtersText} </p>
                    </ButtonType1>
                }
                <Select
                    $color={cssVars.colorGrey[3]}
                    $focusColor={cssVars.colorGrey[0]}
                    $borderColor={cssVars.colorGrey[2]}
                    $width="10rem"
                    $padding={[0.575, 0.375]}
                    $fontSize={$fontSize}
                    onChange={onChangeSortByColumn}
                    options={sortColumnOptions}
                    defaultValue={sortColumnOptions[0]}
                    placeholder="Ordenar por"
                    disabled={loadingState.value}
                    leftIcon={<FaSort />}
                    disableClearButton
                />
                <Select<boolean>
                    $color={cssVars.colorGrey[3]}
                    $focusColor={cssVars.colorGrey[0]}
                    $borderColor={cssVars.colorGrey[2]}
                    $width="10rem"
                    $padding={[0.575, 0.375]}
                    $fontSize={$fontSize}
                    defaultValue={{ name: "Crescente", value: true }}
                    options={[{ name: "Crescente", value: true }, { name: "Decrescente", value: false }]}
                    onChange={onChangeSortTypeColumn}
                    disabled={loadingState.value}
                    disableClearButton
                    leftIcon={<FaSort />}
                />
                <MultiSelect
                    $color={cssVars.colorGrey[3]}
                    $borderColor={cssVars.colorGrey[2]}
                    $padding={[0.525, 0.375]}
                    $fontSize={$fontSize}
                    $width="12rem"
                    leftIcon={<FaTableColumns />}
                    onChange={onChangeDisplayColumns}
                    options={displayColumnsOptions}
                    placeholder="Colunas"
                    defaultValue={displayColumnsOptions}
                />
            </div>
        </StyledTableHeader>
    );
}

export default TableHeader;